import * as React from 'react';

import { config } from '@rematter/config';
import { Button, ensureValue } from '@rematter/ui-web';
import { LucideAlertTriangle, LucideRefreshCw } from 'lucide-react';

interface Props {
  children: React.ReactNode;
  fallback?: (error: Error, resetError: () => void) => React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console in development
    if (config.isDevelopment) {
      console.error('Error caught by boundary:', error, errorInfo);
    }

    // You can also log the error to an error reporting service here
    // Example: logErrorToService(error, errorInfo);
  }

  resetError = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(
          ensureValue(this.state.error),
          this.resetError,
        );
      }

      // Default fallback UI
      return (
        <div className="@ErrorBoundary flex min-h-screen items-center justify-center bg-neutral/5 p-4">
          <div className="w-full max-w-1/2 rounded-lg bg-white p-6 text-center shadow-lg">
            <div className="mb-4 flex justify-center">
              <div className="flex size-16 items-center justify-center rounded-full bg-failure/10">
                <LucideAlertTriangle className="size-8 text-failure" />
              </div>
            </div>

            <h1 className="mb-2 text-2xl font-semibold text-neutral/80">
              Something went wrong
            </h1>

            <p className="mb-6 text-neutral">
              We encountered an unexpected error. The issue has been logged and
              our team will look into it.
            </p>

            {config.isDevelopment && this.state.error && (
              <details className="mb-6 text-left">
                <summary className="cursor-pointer text-sm text-neutral/80 hover:text-neutral/70">
                  Error details (development only)
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-neutral/10 p-3 text-xs">
                  {this.state.error.toString()}
                  {this.state.error.stack && '\n\n' + this.state.error.stack}
                </pre>
              </details>
            )}

            <div className="flex justify-center gap-3">
              <Button
                variant="outline"
                onClick={() => (window.location.href = '/')}
              >
                Go to Home
              </Button>
              <Button onClick={this.resetError} color="primary">
                <LucideRefreshCw className="mr-2 size-4" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
